import React, { useEffect, useState } from 'react';
import { projectFirestore } from '../../firebase/config';
import useVisitorStats from '../../hooks/useVisitorStats';

function formatDate(ms) {
  if (!ms) return 'غير متاح';
  return new Date(ms).toLocaleString('ar-EG');
}

const UserStatsPanel = () => {
  const [periodDays, setPeriodDays] = useState(null);
  const [counterEnabled, setCounterEnabled] = useState(true);
  const [savingCounter, setSavingCounter] = useState(false);

  const {
    loading,
    totalVisitors,
    onlineVisitors,
    todayVisitors,
    activeLast24h,
    adminUsersCount,
    onlineAdminSessions,
    recentVisitors,
    lastUpdatedAt,
  } = useVisitorStats(periodDays);

  useEffect(() => {
    const unsub = projectFirestore.collection('settings').doc('site').onSnapshot((doc) => {
      if (!doc.exists) {
        setCounterEnabled(true);
        return;
      }
      const data = doc.data();
      setCounterEnabled(data.showGlobalVisitorCounter !== false);
    });

    return () => unsub();
  }, []);

  const saveCounterToggle = async (nextValue) => {
    setSavingCounter(true);
    try {
      await projectFirestore.collection('settings').doc('site').set(
        { showGlobalVisitorCounter: nextValue },
        { merge: true }
      );
      setCounterEnabled(nextValue);
    } finally {
      setSavingCounter(false);
    }
  };

  if (loading) {
    return (
      <div className="card shadow-sm my-3">
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2">جاري تحميل إحصائيات الزوار...</div>
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'إجمالي الزوار', value: totalVisitors, color: 'primary' },
    { label: 'المتصلون الآن', value: onlineVisitors, color: 'success' },
    { label: 'زوار اليوم', value: todayVisitors, color: 'info' },
    { label: 'نشطون آخر 24 ساعة', value: activeLast24h, color: 'warning' },
    { label: 'حسابات الأدمن', value: adminUsersCount, color: 'secondary' },
    { label: 'جلسات أدمن متصلة', value: onlineAdminSessions, color: 'dark' },
  ];

  return (
    <div className="container my-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h5 className="mb-0">إحصائيات المستخدمين والزوار</h5>
        <small className="text-muted">آخر تحديث: {formatDate(lastUpdatedAt)}</small>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div className="fw-bold">عرض العداد في كل صفحات الموقع</div>
            <small className="text-muted">يمكن تشغيله أو إخفاؤه للزوار من هنا.</small>
          </div>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="globalCounterSwitch"
              checked={counterEnabled}
              disabled={savingCounter}
              onChange={(e) => saveCounterToggle(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="globalCounterSwitch">
              {counterEnabled ? 'مفعل' : 'متوقف'}
            </label>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex align-items-center flex-wrap gap-2">
          <span className="fw-bold">فلتر المدة:</span>
          <button
            className={`btn btn-sm ${periodDays === null ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPeriodDays(null)}
          >
            كل الوقت
          </button>
          <button
            className={`btn btn-sm ${periodDays === 1 ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPeriodDays(1)}
          >
            اليوم
          </button>
          <button
            className={`btn btn-sm ${periodDays === 7 ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPeriodDays(7)}
          >
            آخر 7 أيام
          </button>
          <button
            className={`btn btn-sm ${periodDays === 30 ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPeriodDays(30)}
          >
            آخر 30 يوم
          </button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {cards.map((item) => (
          <div className="col-12 col-sm-6 col-lg-4" key={item.label}>
            <div className={`card border-${item.color} shadow-sm h-100`}>
              <div className="card-body">
                <div className="text-muted" style={{ fontSize: 13 }}>{item.label}</div>
                <div className={`display-6 fw-bold text-${item.color}`}>{item.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h6 className="mb-3">آخر الزوار نشاطًا</h6>
          {recentVisitors.length === 0 ? (
            <p className="text-muted mb-0">لا توجد بيانات زيارات حتى الآن.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>المعرف</th>
                    <th>المسار الحالي</th>
                    <th>الحالة</th>
                    <th>آخر ظهور</th>
                    <th>أول زيارة</th>
                  </tr>
                </thead>
                <tbody>
                  {recentVisitors.map((visitor) => (
                    <tr key={visitor.id}>
                      <td>{visitor.id}</td>
                      <td>{visitor.currentPath || '/'}</td>
                      <td>
                        {visitor.isOnline && visitor.activeByTime
                          ? <span className="badge bg-success">متصل</span>
                          : <span className="badge bg-secondary">غير متصل</span>}
                      </td>
                      <td>{formatDate(visitor.lastSeenMs)}</td>
                      <td>{formatDate(visitor.firstSeenMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserStatsPanel;
